import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useModalFocusTrap } from '../../../accessibility/useModalFocusTrap';

export function StartInvestigationDialog({
  title: initialTitle = '', objective: initialObjective = '', busy = false, error = null, mode = 'create', onClose, onSubmit,
}: {
  title?: string;
  objective?: string;
  busy?: boolean;
  error?: string | null;
  mode?: 'create' | 'promote';
  onClose: () => void;
  onSubmit: (title: string, objective: string) => Promise<void> | void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [objective, setObjective] = useState(initialObjective);
  const dialogRef = useRef<HTMLFormElement>(null);
  useModalFocusTrap(dialogRef, true, busy ? undefined : onClose);
  useEffect(() => { setTitle(initialTitle); setObjective(initialObjective); }, [initialTitle, initialObjective]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !objective.trim() || busy) return;
    void onSubmit(title.trim(), objective.trim());
  }

  return (
    <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="investigation-dialog-title" aria-describedby="investigation-dialog-description">
      <button className="r42-dialog-backdrop" type="button" tabIndex={-1} aria-label="Close investigation dialog" onClick={onClose} disabled={busy} />
      <form ref={dialogRef} tabIndex={-1} className="r42-dialog-card r42-investigation-dialog" onSubmit={submit}>
        <span className="r42-eyebrow">{mode === 'promote' ? 'Keep working on this problem' : 'Shared problem-solving workspace'}</span>
        <h2 id="investigation-dialog-title">{mode === 'promote' ? 'Start an investigation from this answer' : 'Start investigation'}</h2>
        <p id="investigation-dialog-description">{mode === 'promote' ? 'Route42 will keep this answer and its supporting evidence as the starting point for the Investigation.' : 'Keep one machine problem, what the team learns, and the Investigation conversation together until you know what to do next.'}</p>
        {error ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 couldn't start the investigation.</strong><span>{error}</span></div></div> : null}
        <div className="r42-investigation-dialog-fields">
          <label className="r42-field"><span className="r42-field__label">Title <b>Required</b></span><input data-autofocus="true" maxLength={200} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Recurring pump failure" /></label>
          <label className="r42-field"><span className="r42-field__label">What are we trying to figure out? <b>Required</b></span><textarea rows={4} maxLength={1200} value={objective} onChange={(event) => setObjective(event.target.value)} placeholder="Understand why the pump keeps losing pressure and what the team should check next." /></label>
        </div>
        <div className="r42-investigation-dialog-learn"><i className="bi bi-lightbulb" aria-hidden="true" /><span>Not sure what an Investigation looks like? <Link to="/learn/investigations">See an Investigation in action</Link>.</span></div>
        <div className="r42-dialog-actions"><button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>Cancel</button><button type="submit" className="btn r42-btn-primary" disabled={busy || !title.trim() || !objective.trim()}>{busy ? 'Starting…' : 'Start investigation'}</button></div>
      </form>
    </div>
  );
}
